// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { RhToast } from "@rhythm-ui/react";

// Define a service using a base URL and expected endpoints
export const mainApi = createApi({
  reducerPath: "mainApi",
  baseQuery: fetchBaseQuery({
    baseUrl: process.env.REACT_APP_API_URL,
    prepareHeaders: (headers) => {
      headers.set("Authorization", `Bearer ${localStorage.getItem("access")}`);
      return headers;
    },
  }),
  tagTypes: [
    "projects",
    "reports",
    "reportByID",
    "services",
    "service",
    "formulae",
    "formula",
    "carbon_coefficients",
    "service_formulae",
    "tags",
    "filterReport",
  ],
  endpoints: (builder) => ({
    login: builder.mutation({
      query: (body) => ({
        method: "post",
        url: "/login/",
        body,
      }),
    }),
    getProject: builder.query({
      query: (params) => ({
        method: "get",
        url: "/project/",
        params,
      }),
      providesTags: ["projects"],
      transformResponse: (data) => data?.data,
    }),
    getProjectById: builder.query({
      query: (id, params) => ({
        method: "get",
        url: `/project/${id}/`,
        params,
      }),
      transformResponse: (data) => data?.data,
    }),
    getAssetCategories: builder.query({
      query: () => ({
        method: "get",
        url: "/asset_class/",
      }),
      transformResponse: (data) => data?.data,
    }),
    addProject: builder.mutation({
      query: (body) => ({
        method: "post",
        url: "/project/",
        body,
      }),
      invalidatesTags: ["projects"],
    }),
    deleteProject: builder.mutation({
      query: (id) => ({
        method: "delete",
        url: `/project/${id}/`,
      }),
      invalidatesTags: ["deleteProject"],
      onQueryStarted({ name }, { queryFulfilled }) {
        queryFulfilled
          .then(() => RhToast.success(`Project deleted successfully!`))
          .catch(() => {
            RhToast.error(`Something went worng!`);
          });
      },
    }),
    addProjectFile: builder.mutation({
      query: (body) => ({
        method: "post",
        url: "/project_file/",
        body,
      }),
      transformResponse: (res) => res?.data,
      invalidatesTags: ["projects"],
    }),
    updateProjectFile: builder.mutation({
      query: ({ id, ...body }) => ({
        method: "put",
        url: `/project_file/${id}/`,
        body,
      }),
      transformResponse: (res) => res?.data,
      invalidatesTags: ["projects"],
    }),
    getReport: builder.query({
      query: (params) => ({
        method: "get",
        url: "/report/",
        params,
      }),
      providesTags: ["reports"],
      transformResponse: (data) => data?.data,
    }),
    getReportById: builder.query({
      query: (id, params) => ({
        method: "get",
        url: `/report/${id}/`,
        params,
      }),
      providesTags: ["reportByID"],
      transformResponse: (data) => data?.data,
    }),
    addReport: builder.mutation({
      query: (body) => ({
        method: "post",
        url: "/report/",
        body,
      }),
      invalidatesTags: ["reports"],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => RhToast.success(`${body.name} added successfully!`))
          .catch(() => RhToast.error(`Unable to add ${body.name}!`));
      },
    }),
    deleteReport: builder.mutation({
      query: (id) => ({
        method: "delete",
        url: `/report/${id}`,
      }),
      invalidatesTags: ["deleteReport"],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => RhToast.success("Report deleted successfully!"))
          .catch(() => RhToast.error(`Unable to delete!`));
      },
    }),
    getService: builder.query({
      query: (params) => ({
        method: "get",
        url: "/service/",
        params,
      }),
      providesTags: ["services"],
      transformResponse: (data) => data?.data,
    }),
    getServiceById: builder.query({
      query: ({ id, ...params }) => ({
        method: "get",
        url: `/service/${id}/`,
        params,
      }),
      providesTags: ["service"],
      transformResponse: (data) => data?.data,
    }),
    addService: builder.mutation({
      query: (body) => ({
        method: "post",
        url: "/service/",
        body,
      }),
      invalidatesTags: ["services"],
      onQueryStarted(body, { queryFulfilled }) {
        queryFulfilled
          .then(() => RhToast.success(`${body.name} added successfully!`))
          .catch(() => RhToast.error(`Unable to add ${body.name}!`));
      },
    }),
    updateService: builder.mutation({
      query: ({ id, ...body }) => ({
        method: "put",
        url: `/service/${id}/`,
        body,
      }),
      invalidatesTags: ["services"],
      onQueryStarted({ name }, { queryFulfilled }) {
        queryFulfilled
          .then(() => RhToast.success(`${name} updated successfully!`))
          .catch(() => RhToast.error(`Unable to update ${name}!`));
      },
    }),
    deleteService: builder.mutation({
      query: ({ id }) => ({
        method: "delete",
        url: `/service/${id}/`,
      }),
      invalidatesTags: ["services"],
      onQueryStarted({ name }, { queryFulfilled }) {
        queryFulfilled
          .then(() => RhToast.success(`${name} deleted successfully!`))
          .catch(() => RhToast.error(`Unable to delete ${name}!`));
      },
    }),
    getCarbonCoefficient: builder.query({
      query: (params) => ({
        method: "get",
        url: "/carbon_coefficient/",
        params,
      }),
      providesTags: ["carbon_coefficients"],
      transformResponse: (data) => data?.data,
    }),
    addCarbonCoefficient: builder.mutation({
      query: (body) => ({
        method: "post",
        url: "/carbon_coefficient/",
        body,
      }),
      invalidatesTags: ["carbon_coefficients"],
    }),
    updateCarbonCoefficient: builder.mutation({
      query: ({ id, ...body }) => ({
        method: "put",
        url: `/carbon_coefficient/${id}/`,
        body,
      }),
      invalidatesTags: ["carbon_coefficients"],
      onQueryStarted({ name }, { queryFulfilled }) {
        queryFulfilled
          .then(() => RhToast.success(`${name} updated successfully!`))
          .catch(() => RhToast.error(`Unable to update ${name}!`));
      },
    }),
    deleteCarbonCoefficient: builder.mutation({
      query: ({ id }) => ({
        method: "delete",
        url: `/carbon_coefficient/${id}/`,
      }),
      invalidatesTags: ["carbon_coefficients"],
      onQueryStarted({ name }, { queryFulfilled }) {
        queryFulfilled
          .then(() => RhToast.success(`${name} deleted successfully!`))
          .catch(() =>
            RhToast.error(
              `Unable to delete ${name} because it is being used in formula!`
            )
          );
      },
    }),
    addFormula: builder.mutation({
      query: (body) => ({
        method: "post",
        url: "/formula/",
        body,
      }),
      invalidatesTags: ["formulae"],
    }),
    updateFormula: builder.mutation({
      query: ({ id, ...body }) => ({
        method: "put",
        url: `/formula/${id}/`,
        body,
      }),
      invalidatesTags: ["formulae"],
    }),
    deleteFormula: builder.mutation({
      query: (id) => ({
        method: "delete",
        url: `/formula/${id}/`,
      }),
      invalidatesTags: ["deleteFormulae"],
      onQueryStarted({ name }, { queryFulfilled }) {
        queryFulfilled
          .then(() => RhToast.success(`Formula deleted successfully!`))
          .catch((res) => {
            console.log(res.error.data.status);
            if (res.error.data.status == 500)
              RhToast.error(
                "Cannot delete Formula beacuse it is part of service"
              );
            else RhToast.error(`Something went worng!`);
          });
      },
    }),
    addFormulaFile: builder.mutation({
      query: (body) => ({
        method: "post",
        url: "/formula_file/",
        body,
      }),
      transformResponse: (res) => res?.data,
      invalidatesTags: ["formulae"],
    }),
    updateFormulaFile: builder.mutation({
      query: ({ id, ...body }) => ({
        method: "put",
        url: `/formula_file/${id}/`,
        body,
      }),
      transformResponse: (res) => res?.data,
      invalidatesTags: ["projects"],
    }),
    getFormula: builder.query({
      query: (params) => ({
        method: "get",
        url: "/formula/",
        params,
      }),
      providesTags: ["formulae"],
      transformResponse: (data) => data?.data,
    }),
    getFormulaById: builder.query({
      query: ({ id, ...params }) => ({
        method: "get",
        url: `/formula/${id}/`,
        params,
      }),
      providesTags: ["formula"],
      transformResponse: (data) => data?.data,
    }),
    getFormulaService: builder.query({
      query: (params) => ({
        method: "get",
        url: "/formula_service/",
        params,
      }),
      providesTags: ["service_formulae"],
      transformResponse: (data) => data?.data,
    }),
    addFormulaService: builder.mutation({
      query: (body) => ({
        method: "post",
        url: "/formula_service/",
        body,
      }),
      invalidatesTags: ["service"],
      transformResponse: (data) => data?.data,
    }),
    deleteFormulaService: builder.mutation({
      query: ({ id }) => ({
        method: "delete",
        url: `/formula_service/${id}/`,
      }),
      invalidatesTags: ["service"],
      transformResponse: (data) => data?.data,
    }),
    getFormulaCoefficient: builder.query({
      query: (params) => ({
        method: "get",
        url: "/coefficient_formula/",
        params,
      }),
      providesTags: ["formula_coefficient"],
      transformResponse: (data) => data?.data,
    }),
    addFormulaCoefficient: builder.mutation({
      query: (body) => ({
        method: "post",
        url: "/coefficient_formula/",
        body,
      }),
      invalidatesTags: ["formula"],
      transformResponse: (data) => data?.data,
    }),
    deleteFormulaCoefficient: builder.mutation({
      query: ({ id }) => ({
        method: "delete",
        url: `/coefficient_formula/${id}/`,
      }),
      transformResponse: (data) => data?.data,
    }),
    getFormulaFileById: builder.query({
      query: (params) => ({
        method: "get",
        url: "/formula_file/",
        params,
      }),
      providesTags: ["formula_files"],
      transformResponse: (data) => data?.data,
    }),

    addCarbonFootprintRecalculation: builder.mutation({
      query: (body) => ({
        method: "post",
        url: "/recalculate_carbon_footprint/",
        body,
      }),
      invalidatesTags: ["reportByID"],
      transformResponse: (data) => data?.data,
    }),
    getTags: builder.query({
      query: (params) => ({
        method: "get",
        url: "/tag/",
        params,
      }),
      providesTags: ["tags"],
      transformResponse: (data) => data?.data,
    }),
    addTag: builder.mutation({
      query: (body) => ({
        method: "post",
        url: "/tag/",
        body,
      }),
      invalidatesTags: ["tags"],
      transformResponse: (data) => data?.data,
    }),
    deleteTag: builder.mutation({
      query: ({ id }) => ({
        method: "delete",
        url: `/tag/${id}/`,
      }),
      transformResponse: (data) => data?.data,
      invalidatesTags: ["tags"],
    }),
    updateTag: builder.mutation({
      query: ({ id, ...body }) => ({
        method: "put",
        url: `/tag/${id}/`,
        body,
      }),
      transformResponse: (res) => res?.data,
      invalidatesTags: ["tags"],
    }),
    getReportFilterData: builder.query({
      query: (params) => ({
        method: "get",
        url: "/report_filter/",
        params,
      }),
      providesTags: ["filterReport"],
      transformResponse: (data) => data?.data,
    }),
  }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useLoginMutation,
  useGetProjectByIdQuery,
  useGetProjectQuery,
  useAddProjectMutation,
  useDeleteProjectMutation,
  useAddProjectFileMutation,
  useUpdateProjectFileMutation,
  useGetAssetCategoriesQuery,
  useGetReportQuery,
  useGetReportByIdQuery,
  useAddReportMutation,
  useDeleteReportMutation,
  useGetServiceQuery,
  useGetServiceByIdQuery,
  useAddServiceMutation,
  useUpdateServiceMutation,
  useDeleteServiceMutation,
  useGetCarbonCoefficientQuery,
  useAddCarbonCoefficientMutation,
  useUpdateCarbonCoefficientMutation,
  useDeleteCarbonCoefficientMutation,
  useAddFormulaMutation,
  useUpdateFormulaMutation,
  useDeleteFormulaMutation,
  useAddFormulaFileMutation,
  useUpdateFormulaFileMutation,
  useGetFormulaQuery,
  useGetFormulaFileByIdQuery,
  useGetFormulaServiceQuery,
  useAddFormulaServiceMutation,
  useDeleteFormulaServiceMutation,
  useLazyGetFormulaQuery,
  useLazyGetFormulaServiceQuery,
  useGetFormulaByIdQuery,
  useLazyGetFormulaByIdQuery,
  useLazyGetCarbonCoefficientQuery,
  useLazyGetFormulaCoefficientQuery,
  useAddFormulaCoefficientMutation,
  useDeleteFormulaCoefficientMutation,
  useAddCarbonFootprintRecalculationMutation,
  useGetTagsQuery,
  useAddTagMutation,
  useDeleteTagMutation,
  useUpdateTagMutation,
  useLazyGetReportFilterDataQuery,
  useLazyGetTagsQuery,
} = mainApi;
